
'use client';

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sunrise, Sun, Sunset, Moon, MapPin, Loader2 } from 'lucide-react';

interface PrayerTime {
  name: string;
  time: string;
  icon: React.ElementType;
}

interface PrayerTimesCardProps {
  lat: number;
  lng: number;
}

const prayerIcons: { [key: string]: React.ElementType } = {
  Fajr: Sunrise,
  Dhuhr: Sun,
  Asr: Sun,
  Maghrib: Sunset,
  Isha: Moon,
};

const getPrayerTimes = async (lat: number, lng: number) => {
    const prayerResponse = await fetch(`https://api.aladhan.com/v1/timings?latitude=${lat}&longitude=${lng}&method=3`);
    if (!prayerResponse.ok) {
        throw new Error('Could not retrieve prayer times for this location.');
    }
    return prayerResponse.json();
};

const getLocationName = async (lat: number, lng: number) => {
    const locationResponse = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
    if (!locationResponse.ok) {
        throw new Error('Could not retrieve location name.');
    }
    return locationResponse.json();
};


export default function PrayerTimesCard({ lat, lng }: PrayerTimesCardProps) {
  const [prayerTimes, setPrayerTimes] = React.useState<PrayerTime[]>([]);
  const [locationName, setLocationName] = React.useState('Loading...');
  const [currentDate, setCurrentDate] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchPrayerData = async () => {
      setLoading(true);
      setError(null);
      setLocationName('Loading...');

      const date = new Date();
      setCurrentDate(date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }));

      try {
        const prayerData = await getPrayerTimes(lat, lng);

        if (prayerData.code === 200 && prayerData.data.timings) {
          const { Fajr, Dhuhr, Asr, Maghrib, Isha } = prayerData.data.timings;
          
          const formatTime = (time: string) => {
              const [hour, minute] = time.split(':');
              const date = new Date();
              date.setHours(parseInt(hour, 10));
              date.setMinutes(parseInt(minute, 10));
              return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
          };

          const formattedTimes = [
            { name: 'Fajr', time: formatTime(Fajr), icon: prayerIcons.Fajr },
            { name: 'Dhuhr', time: formatTime(Dhuhr), icon: prayerIcons.Dhuhr },
            { name: 'Asr', time: formatTime(Asr), icon: prayerIcons.Asr },
            { name: 'Maghrib', time: formatTime(Maghrib), icon: prayerIcons.Maghrib },
            { name: 'Isha', time: formatTime(Isha), icon: prayerIcons.Isha },
          ];
          setPrayerTimes(formattedTimes);

          try {
            const locationData = await getLocationName(lat, lng);
            const city = locationData.address.city || locationData.address.town || locationData.address.village || locationData.address.suburb || 'Unknown';
            setLocationName(city);
          } catch (locationError) {
            console.error("Could not fetch location name:", locationError);
            setLocationName('Unknown');
          }

        } else {
           throw new Error('Invalid data format from prayer times API.');
        }

      } catch (err: any) {
        setError(err.message || 'An unknown error occurred.');
        setLocationName('Error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPrayerData();
  }, [lat, lng]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      );
    }

    if (error) {
      return <p className="text-center text-destructive text-xs p-4">{error}</p>;
    }

    return (
       <div className="grid grid-cols-5 gap-2 text-center">
        {prayerTimes.map((prayer) => {
          const Icon = prayer.icon;
          return (
            <div key={prayer.name} className="flex flex-col items-center justify-center p-1 rounded-lg bg-background">
              <p className="text-xs font-medium text-muted-foreground">{prayer.name}</p>
              <Icon className="w-4 h-4 my-1 text-primary" />
              <p className="text-xs font-semibold text-foreground">{prayer.time}</p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Card className="bg-muted/50 border-border">
      <CardHeader className="pb-2 pt-4">
        <CardTitle className="text-base font-semibold text-foreground flex justify-between items-center">
          <span>Prayer Times</span>
           <span className="text-sm font-normal text-muted-foreground flex items-center gap-1 capitalize">
            <MapPin className="w-3 h-3" />
            {locationName}
          </span>
        </CardTitle>
        <p className="text-xs text-muted-foreground pt-0">{currentDate}</p>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {renderContent()}
      </CardContent>
    </Card>
  );
}
